import { StationInsight } from './insights.types';

export type MessageRole = 'user' | 'assistant';

// Widget types for AI responses
export type WidgetType = 'Header' | 'LinkButtons' | 'Text' | 'SpaceLine' | 'Insight';

export interface LinkButton {
  urlText: string;
  redirectUrl: string;
}

// Header title variants
export type HeaderTitleVariant = 'h1' | 'h2' | 'h3' | 'h4';

// Suffix icon names for Header widget
export type SuffixIconName = 'newLabel' | 'packageCare';

// Base animation props shared by all widgets
export interface BaseWidgetAnimationProps {
  shouldStart?: boolean; // Control when animation should start
  onAnimationComplete?: () => void; // Callback when animation finishes
}

// Header widget parameters
export interface HeaderWidgetParams extends BaseWidgetAnimationProps {
  title: string;
  titleVariant?: HeaderTitleVariant;
  suffixIconName?: SuffixIconName | string;
}

// LinkButtons widget parameters
export interface LinkButtonsWidgetParams extends BaseWidgetAnimationProps {
  linkButtons: LinkButton[];
}

// Text widget parameters
export interface TextWidgetParams extends BaseWidgetAnimationProps {
  content: string;
  isStreaming?: boolean;
}

// SpaceLine widget parameters
export interface SpaceLineWidgetParams extends BaseWidgetAnimationProps {
  lines?: number; // Number of blank lines (default: 1)
}

// Insight widget parameters
export interface InsightWidgetParams extends BaseWidgetAnimationProps {
  content: object | string; // Structured insight data (object) or JSON string
  isStreaming?: boolean;
  onPublishClick?: (insightId: string) => void;
  onViewTrendClick?: (insightId: string) => void;
  onViewAlarmsClick?: (insightId: string) => void;
}

// Union type for all widget parameters
export type WidgetParams =
  | HeaderWidgetParams
  | LinkButtonsWidgetParams
  | TextWidgetParams
  | SpaceLineWidgetParams
  | InsightWidgetParams;

// Legacy type alias for backward compatibility (deprecated - use specific widget param types)
/** @deprecated Use specific widget param types (HeaderWidgetParams, TextWidgetParams, etc.) */
export type WidgetParamsType = WidgetParams;

export interface BaseWidget {
  type: WidgetType;
  params?: WidgetParams;
}

export interface ChatMessage {
  id: string;
  role: MessageRole;
  content: string;
  timestamp: Date;
  stationId?: string;
  isStreaming?: boolean;
  widgets?: BaseWidget[]; // Widget-based response structure
  messageId?: string | null; // Backend message ID for matching responses
  isFromHistory?: boolean; // true if message was loaded from backend/history (should not animate)
  hasTimeoutError?: boolean; // true if message timed out waiting for response
}

export interface ChatConversation {
  id: string;
  messages: ChatMessage[];
  stationId?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface StationContext {
  stationId: string;
  stationName?: string;
  currentInsights?: StationInsight[];
  recentAlerts?: unknown[];
}

export interface SuggestedPrompt {
  id: string;
  label: string;
  prompt: string;
  icon?: string;
  badge?: string;
}

// Socket message types
export type ResponseType = 'text' | 'insight';

// Chat message payload sent to the socket
export interface SocketChatMessagePayload {
  sessionId: string;
  messageId: number;
  message: string;
  messageType: string;
  insightId?: string;
}

// Socket response type enum
export const SocketResponseType = {
  TEXT: 'text',
  INSIGHT: 'insight',
  ERROR: 'error',
} as const;

// Chunked free text response format (for "text" and "error" types)
export interface SocketChunkResponse {
  type: 'text' | 'error';
  session_id: string;
  message_id: number;
  sequence: number;
  content: string;
  is_final: boolean;
}

// Insight response format (incremental JSON chunks)
export interface SocketInsightResponse {
  type: 'insight';
  session_id: string;
  message_id: number;
  content: object; // Incremental JSON object that gets merged
  is_final: boolean;
}

export interface SocketChatResponse {
  eventName: 'chatResponse';
  payload: {
    conversationId: string;
    messageId: string;
    type?: ResponseType; // Response type: 'text' or 'insight'
    content?: string | object; // Text content (string) or JSON object for structured responses
    isComplete: boolean; // true if this is the final response, false if streaming
    sequence?: number; // Sequence number for chunked responses (for ordering)
  };
}

export interface SocketErrorResponse {
  eventName: 'chatError';
  payload: {
    conversationId: string;
    error: string;
  };
}
